/**
 * QuoteFinancingSection.tsx
 *
 * The Financing Options band: a payment method plus the terms offered on it,
 * each showing what the quote would cost per month.
 *
 * Legacy stores the checked terms as one semicolon-joined string on a single
 * row (quote_financing.paymentMethodTermID), not a row per term — the service
 * layer does that joining, so this component works in plain arrays.
 */
import { useQuery } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import {
  getPaymentMethodCatalog,
  type CatalogPaymentMethod,
  type QuoteFinancing,
} from '@/services/quotes.service';
import { QuoteSectionCard, EmptySectionRow } from './QuoteSectionCard';

/**
 * Standard amortization, matching quote-financing-display-new.php:62-70.
 * Shown for guidance while editing; nothing is billed from this figure.
 */
function monthlyPayment(principal: number, aprPercent: number, months: number): number {
  if (months <= 0) return 0;
  if (aprPercent === 0) return principal / months;

  const rate = aprPercent / 100 / 12;
  return (principal * rate) / (1 - Math.pow(1 + rate, -months));
}

interface Props {
  financing: Partial<QuoteFinancing>[];
  quoteTotal: number;
  readOnly: boolean;
  onChange: (financing: Partial<QuoteFinancing>[]) => void;
}

export function QuoteFinancingSection({ financing, quoteTotal, readOnly, onChange }: Props) {
  const { data: methods } = useQuery({
    queryKey: ['quote-payment-method-catalog'],
    queryFn: getPaymentMethodCatalog,
    staleTime: 300_000,
  });

  function patch(index: number, changes: Partial<QuoteFinancing>) {
    onChange(financing.map((row, i) => (i === index ? { ...row, ...changes } : row)));
  }

  function add() {
    onChange([...financing, { paymentMethodId: 0, termIds: [] }]);
  }

  return (
    <QuoteSectionCard title="Financing Options" onAdd={readOnly ? undefined : add} addLabel="Add">
      {financing.length === 0 ? (
        <EmptySectionRow>No financing options on this quote.</EmptySectionRow>
      ) : (
        <div className="space-y-3">
          {financing.map((row, index) => {
            const method = (methods ?? []).find(
              (m: CatalogPaymentMethod) => m.paymentMethodId === row.paymentMethodId,
            );
            const selected = row.termIds ?? [];

            return (
              <div key={row.quoteFinancingId ?? `new-${index}`} className="rounded-md border border-slate-200 p-3">
                <div className="flex flex-wrap items-end justify-between gap-2">
                  <label className="text-xs text-slate-600">
                    Payment Method
                    <select
                      className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-100"
                      value={row.paymentMethodId || ''}
                      disabled={readOnly}
                      onChange={(e) =>
                        patch(index, {
                          paymentMethodId: e.target.value ? Number(e.target.value) : 0,
                          // Terms belong to a method, so a new method clears them.
                          termIds: [],
                        })
                      }
                    >
                      <option value="">Select one…</option>
                      {(methods ?? []).map((m: CatalogPaymentMethod) => (
                        <option key={m.paymentMethodId} value={m.paymentMethodId}>{m.name}</option>
                      ))}
                    </select>
                  </label>

                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => onChange(financing.filter((_, i) => i !== index))}
                      className="mb-1 rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 touch-target-44 tap-feedback"
                      aria-label="Remove financing option"
                    >
                      <Trash2 className="h-4 w-4 touch-target-icon" />
                    </button>
                  )}
                </div>

                {method && method.terms.length > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-3">
                    {method.terms.map((term) => {
                      const checked = selected.includes(term.termId);

                      return (
                        <label
                          key={term.termId}
                          className="flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm touch-target-h"
                        >
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            checked={checked}
                            disabled={readOnly}
                            onChange={(e) =>
                              patch(index, {
                                termIds: e.target.checked
                                  ? [...selected, term.termId]
                                  : selected.filter((id) => id !== term.termId),
                              })
                            }
                          />
                          <span className="text-slate-700">
                            {term.months} months
                            {term.apr > 0 ? ` at ${term.apr}%` : ' interest free'}
                          </span>
                          <span className="font-medium text-slate-900">
                            {formatCurrency(monthlyPayment(quoteTotal, term.apr, term.months))}/mo
                          </span>
                        </label>
                      );
                    })}
                  </div>
                ) : method ? (
                  <p className="mt-3 text-sm italic text-slate-400">
                    No terms are set up for this payment method.
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </QuoteSectionCard>
  );
}
